import { Fragment, VNode } from 'preact';
import { Text as T, useTranslator } from '@eo-locale/preact';
import { lazy, Suspense } from 'preact/compat';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { ReviewSorting } from 'types/review';
import { ExtendedReviewsWidgetSettings } from 'types/settings';
import { WidgetRequest } from 'types/widget';
import { useReviews } from 'utilities/api/use-reviews';
import { useDebounce } from 'utilities/use-debounce';
import { useSorting } from 'utilities/use-sorting';

import { ActionList } from 'components/ActionList';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { useModal } from 'components/ModalProvider';
import { Pagination } from 'components/Pagination';
import { useSettings } from 'components/SettingsProvider';
import { Spinner } from 'components/Spinner';
import { Stack } from 'components/Stack';
import { Text } from 'components/Text';
import { Tip } from 'components/Tip';

import { AddReviewModal } from './components/AddReviewModal';
import { Header } from './components/Header';
import { ReviewCard } from './components/ReviewCard';
import { SkeletonContent } from './components/SkeletonContent';
import { SortingKeyToLabel } from './types/sorting';
import { useFilters } from './utilities/use-filters';

import styles from './ReviewsWidget.module.scss';

const ImageGrid = lazy(() => import('components/ImageGrid'));

export function ReviewsWidget() {
  const { translate } = useTranslator();
  const {
    show_gallery,
    reviews_per_page,
    show_reviews_count,
    settings,
    title_tag,
  } = useSettings<ExtendedReviewsWidgetSettings>();
  const { openModal } = useModal();
  const { filters, changeRatingFilter } = useFilters();
  const { sortBy, sortingOptions, changeSorting } = useSorting();
  const [page, setPage] = useState(1);

  const handleScroll = useCallback((node: HTMLElement | null) => {
    if (!window.location.hash.includes('#wbtx-reviews') || node === null) {
      return;
    }

    const rect = node.getBoundingClientRect();

    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    window.scrollTo({
      top: rect.top + scrollTop,
      behavior: 'smooth',
    });
  }, []);

  const request = useMemo<WidgetRequest>(
    () => ({
      components: {
        images: null,
        rating_details: true,
      },
      count: reviews_per_page,
      filters,
      limit: null,
      page,
      sort: sortBy,
    }),
    [page, filters, reviews_per_page, sortBy],
  );

  useEffect(() => {
    setPage(1);
  }, [filters, sortBy]);

  const isShowSummary = settings.reviews.ai?.summary_section?.show_on_front;

  const debouncedRequest = useDebounce<WidgetRequest>(request, 250);

  const handleNextPage = useCallback(() => setPage((page) => page + 1), []);
  const handlePrevPage = useCallback(() => setPage((page) => page - 1), []);
  const handlePage = useCallback((index: number) => setPage(index), []);

  const { reviews, meta, isLoading, isFetching } = useReviews(debouncedRequest);

  const totalPages = Math.ceil(meta.total / meta.count);
  const overallRating =
    reviews.length > 0 ? reviews[0].product_overall_rating : 0;

  const summary = reviews.length > 0 ? reviews[0].product_summary : '';

  if (isLoading) {
    return <SkeletonContent />;
  }

  const imageGridMarkup = show_gallery && <ImageGrid />;

  const actions: VNode[] = [];

  if (settings.reviews.general.allow_guest_reviews) {
    actions.push(
      <Button key="review-button" primary rounded onClick={openModal}>
        <T
          id="write_review_write_review_button"
          defaultMessage="Write a review"
        />
      </Button>,
    );
  }

  if (meta.total > 0 && sortingOptions.length > 0) {
    const activator = (
      <Button rounded iconPlacement="right">
        <Stack alignment="baseline" spacing="extraTight">
          <Text size="small" as="span" variation="subdued">
            <T id="reviews_list_sort_by_label" defaultMessage="Sort by" />:
          </Text>
          <Text as="span">
            {translate(SortingKeyToLabel[sortBy], {
              defaultMessage: SortingKeyToLabel[sortBy],
            })}
          </Text>
        </Stack>
      </Button>
    );

    actions.push(
      <ActionList
        key="sorting"
        selected={sortBy}
        title={translate('reviews_list_sort_by_label', {
          defaultMessage: 'Sort by',
        })}
        options={sortingOptions}
        activator={activator}
        onChange={(value) => changeSorting(value as ReviewSorting)}
      />,
    );
  }

  const aiSummaryMarkup = isShowSummary && summary.length > 0 && (
    <div className={styles.SummaryWrapper}>
      <Card sectioned subdued rounded={false}>
        <Stack vertical spacing="tight">
          <Stack spacing="tight">
            <Text size="large" variation="semiStrong">
              {translate('ai_summary_title', {
                defaultMessage: 'Reviews summary',
              })}
            </Text>
            {translate('ai_summary_description').length > 0 && (
              <Tip
                content={
                  <Stack vertical>
                    <Text size="small">
                      {translate('ai_summary_description')}
                    </Text>
                  </Stack>
                }
              />
            )}
          </Stack>
          <Text size="small">{summary}</Text>
        </Stack>
      </Card>
    </div>
  );

  const headerMarkup =
    reviews.length > 0 ? (
      <Header
        actions={actions}
        ratingDetails={meta.rating_details}
        totalReviews={meta.total}
        overallRating={overallRating}
        onFilterClick={changeRatingFilter}
      />
    ) : null;

  const reviewsMarkup =
    reviews.length > 0 ? (
      reviews.map((review) => <ReviewCard key={review.id} review={review} />)
    ) : (
      <Stack classNames={['wbtxReviews__empty']}>
        <Stack.Item fill>
          <Card sectioned subdued rounded={false}>
            <Stack>
              <Text classNames={['wbtxReviews__empty-text']}>
                <T
                  html
                  id="widget_reviews_no_product_reviews_message"
                  defaultMessage="This product doesn't have reviews yet. Be the first to <a href='#add-review'>write a review</a>"
                />
              </Text>
            </Stack>
          </Card>
        </Stack.Item>
        {settings.reviews.general.allow_guest_reviews && (
          <Button primary rounded onClick={openModal}>
            <T
              id="write_review_write_review_button"
              defaultMessage="Write a review"
            />
          </Button>
        )}
      </Stack>
    );

  const paginationMarkup = totalPages > 1 && (
    <Stack distribution="trailing" classNames={['wbtxReviews__pagination']}>
      <Pagination
        totalPages={totalPages}
        currentPage={page}
        hasNext={page < totalPages}
        hasPrev={page > 1}
        onPrev={handlePrevPage}
        onNext={handleNextPage}
        onPage={handlePage}
      />
    </Stack>
  );

  const modalMarkup = settings.reviews.general.allow_guest_reviews && (
    <AddReviewModal />
  );

  const spinnerMarkup = isFetching && (
    <div className={styles.LoadingOverlay}>
      <Spinner />
    </div>
  );

  const titleMarkup = show_reviews_count ? (
    <Fragment>
      <T id="widget_reviews_title" defaultMessage="Reviews" /> ({meta.total})
    </Fragment>
  ) : (
    <T id="widget_reviews_title" defaultMessage="Reviews" />
  );

  return (
    <section
      ref={handleScroll}
      className={styles.ReviewsWidget}
      id="wbtx-reviews"
    >
      {modalMarkup}
      <Stack vertical wrap={false}>
        <Text
          size="extraLarge"
          as={title_tag || 'h2'}
          variation="strong"
          classNames={['wbtxReviews__title']}
        >
          {titleMarkup}
        </Text>
        {aiSummaryMarkup}
        {headerMarkup}
        <Suspense fallback={null}>{imageGridMarkup}</Suspense>
        {reviewsMarkup}
        {paginationMarkup}
      </Stack>
      {spinnerMarkup}
    </section>
  );
}
